import React, { useState, useEffect, useRef } from 'react';
import { 
  getImageFiles, createStampByObject, 
  createRgbImage, createRgbByCoordinates, createRgbByObject,
  createTrilogyImage, createTrilogyByCoordinates, createTrilogyByObject,
  createStampByCoordinates 
} from '../api/images';

import { RgbTool } from './image/Lupton';
import StampTool from './image/Stamp';
import TrilogyTool from './image/Trilogy';
import BatchStampsTool from './image/BatchStamps';

// Main Image Tools component
const ImageTools = ({ collectionId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [expandedTool, setExpandedTool] = useState(null);
  
  // Load files when collection ID changes
  useEffect(() => {
    if (collectionId) {
      loadFiles();
    } else {
      setFiles([]);
    }
  }, [collectionId]);
  
  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getImageFiles(collectionId);
      setFiles(data);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load image files');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleToolToggle = (toolName) => {
    setExpandedTool(expandedTool === toolName ? null : toolName);
  };

  if (!collectionId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Image Tools</h1>
        <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
          <p className="text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
            Please select an image collection to use these tools.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Image Tools</h1>
        {loading && (
          <div className="flex items-center">
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading...</span>
          </div>
        )}
      </div>
      
      {error && (
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          {error}
        </div>
      )}
      
      {/* Tool Cards */}
      <div className="space-y-4">
        {/* Stamp Tool Card */}
        <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
          <button 
            className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
            onClick={() => handleToolToggle('stamp')}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Stamp Tool</h2>
                <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Create stamp images from a selected area
                </p>
              </div>
            </div>
            <svg 
              className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'stamp' ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          
          {expandedTool === 'stamp' && (
            <div className="px-6 pb-6">
              <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
              <StampTool collectionId={collectionId} availableFiles={files} />
            </div>
          )}
        </div>
        
        {/* RGB Tool Card */}
        <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
          <button 
            className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
            onClick={() => handleToolToggle('rgb')}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">RGB Tool</h2>
                <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Create color composite images using multiple filters
                </p>
              </div>
            </div>
            <svg 
              className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'rgb' ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          
          {expandedTool === 'rgb' && (
            <div className="px-6 pb-6">
              <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
              <RgbTool availableFiles={files} collectionId={collectionId} />
            </div>
          )}
        </div>
        
        {/* Batch Stamps Tool Card */}
        <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
          <button
            className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
            onClick={() => handleToolToggle('batch')}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Batch Stamps</h2>
                <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Create multiple stamps from a CSV file (max 100)
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'batch' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>

          {expandedTool === 'batch' && (
            <div className="px-6 pb-6">
              <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
              <BatchStampsTool collectionId={collectionId} availableFiles={files} />
            </div>
          )}
        </div>

        {/* Trilogy Tool Card */}
        <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
          <button
            className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
            onClick={() => handleToolToggle('trilogy')}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Trilogy Tool</h2>
                <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Advanced visualization with enhanced processing options
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'trilogy' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>

          {expandedTool === 'trilogy' && (
            <div className="px-6 pb-6">
              <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
              <TrilogyTool collectionId={collectionId} availableFiles={files} />
            </div>
          )}
        </div>
      </div>
      
      {/* Notification */}
      {notification.open && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-md shadow-lg ${
          notification.severity === 'error' 
            ? 'bg-status-error-light-bg text-status-error-light-text dark:bg-status-error-dark-bg dark:text-status-error-dark-text' 
            : notification.severity === 'success'
            ? 'bg-status-success-light-bg text-status-success-light-text dark:bg-status-success-dark-bg dark:text-status-success-dark-text'
            : notification.severity === 'warning'
            ? 'bg-status-warning-light-bg text-status-warning-light-text dark:bg-status-warning-dark-bg dark:text-status-warning-dark-text'
            : 'bg-status-info-light-bg text-status-info-light-text dark:bg-status-info-dark-bg dark:text-status-info-dark-text'
        }`}>
          <div className="flex items-center justify-between">
            <p>{notification.message}</p>
            <button 
              onClick={handleClose}
              className="ml-4 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageTools;